import PptxGenJS from 'pptxgenjs';
import { Present } from '../api/present/Present';
import { Widget } from '../types/dashboards/dashboard';
import html2canvas from 'html2canvas';

interface TextType {
	text: string;
	format: any[] | null;
	fontSize: number;
}

export const createPresentation = (
	pageModel: Present,
	setIsExporting: React.Dispatch<React.SetStateAction<boolean>>,
	closeWidgetsLibrarySidebar?: () => void
) => {
	setIsExporting(true);
	setTimeout(() => {
		const pptx = new PptxGenJS();
		pptx.author = 'ZeroedIn Technologies, LLC';
		pptx.company = 'ZeroedIn Technologies, LLC';
		pptx.revision = '1';
		pptx.subject = pageModel.name;
		pptx.title = pageModel.name;

		const imagePromises: Promise<void>[] = [];
		const hiddenSlide = document.querySelector(
			'#presentation-hidden-render .react-grid-layout'
		);
		const slideWidth = (hiddenSlide as HTMLElement).offsetWidth;
		const slideHeight = (hiddenSlide as HTMLElement).offsetHeight;
		const widthScaleFactor = 10 / calculateInches(slideWidth);
		const heightScaleFactor = 5.625 / calculateInches(slideHeight);
		// One factor for both axes so captured images keep their aspect ratio
		const imageScaleFactor = Math.min(widthScaleFactor, heightScaleFactor);
		// html2canvas defaults to devicePixelRatio, which is too soft for a projected slide on a 1x screen
		const captureScale = 2;
		pageModel.presentationSlides.forEach((s) => {
			// Default Size: 10 x 5.625 inches
			const slide = pptx.addSlide();
			(s.widgets as Widget[]).forEach((w: Widget) => {
				const horizontalPaddingInches = calculateInches(10) * widthScaleFactor;
				const verticalPaddingInches = calculateInches(10) * heightScaleFactor;
				if (w.widget_type === 'text') {
					const textObjects = htmlToText(w.content ?? '');
					const texts: PptxGenJS.TextProps[] = [];
					textObjects.forEach((textObject: TextType) => {
						const isBold = textObject.format?.includes('strong');
						const isItalic = textObject.format?.includes('em');
						const isUnderlined = textObject.format?.includes('ins');
						texts.push({
							text: textObject.text ?? '',
							options: {
								bold: isBold,
								italic: isItalic,
								underline: isUnderlined ? { style: 'sng' } : { style: 'none' },
								fontSize: textObject.fontSize,
							},
						});
					});

					slide.addText(texts, {
						x: calculateInches(w.col * 75) + horizontalPaddingInches,
						y: calculateInches(w.row * 40) + verticalPaddingInches,
						w: calculateInches(w.sizex * 73),
						h: calculateInches(w.sizey * 42) * heightScaleFactor,
						valign: 'top',
					});
				} else if (w.widget_type == 'chart') {
					// TODO: Add slide to chart
					const chartElem = document.querySelector(
						`#presentation-hidden-render #widget-${w.id ?? 0}`
					);
					if (chartElem) {
						const chartWidthinPX = (chartElem as HTMLElement).offsetWidth;
						const chartHeightinPX = (chartElem as HTMLElement).offsetHeight;
						const chartElement = chartElem as HTMLElement;

						// Detect chart type directly from Highcharts SVG <g> class
						const svgElem = chartElement.querySelector('svg');
						let chartType = 'other';

						if (svgElem) {
							const seriesGroups = Array.from(
								svgElem.querySelectorAll('g.highcharts-series')
							);
							for (const g of seriesGroups) {
								const className = g.getAttribute('class')?.toLowerCase() || '';
								if (
									className.includes('pie-series') ||
									className.includes('donut-series')
								) {
									chartType = 'donut'; // pie or donut
									break;
								} else if (
									className.includes('bar-series') ||
									className.includes('column-series')
								) {
									chartType = 'bar';
									break;
								} else if (
									className.includes('line-series') ||
									className.includes('area-series')
								) {
									chartType = 'line';
									break;
								}
							}
						}

						imagePromises.push(
							html2canvas(chartElem as HTMLElement, { scale: captureScale }).then(
								(canvas: HTMLCanvasElement) => {
									slide.addImage({
										x: calculateInches(w.col * 80) + horizontalPaddingInches,
										y: calculateInches(w.row * 36) + horizontalPaddingInches,
										w: calculateInches(chartWidthinPX) * imageScaleFactor,
										h: calculateInches(chartHeightinPX) * imageScaleFactor,
										data: canvas.toDataURL(),
									});
								}
							)
						);
					}
				} else if (w.widget_type == 'pivot_table') {
					// TODO: Add slide to chart
					const chartElem = document.querySelector(
						`#presentation-hidden-render #widget-${w.id ?? 0}`
					);
					if (chartElem) {
						const chartWidthinPX = (chartElem as HTMLElement).offsetWidth;
						const chartHeightinPX = (chartElem as HTMLElement).offsetHeight;
						imagePromises.push(
							html2canvas(chartElem as HTMLElement, { scale: captureScale }).then(
								(canvas: HTMLCanvasElement) => {
									slide.addImage({
										x: calculateInches(w.col * 80) + horizontalPaddingInches,
										y: calculateInches(w.row * 36) + horizontalPaddingInches,
										w: calculateInches(chartWidthinPX) * imageScaleFactor,
										h: calculateInches(chartHeightinPX) * imageScaleFactor,
										data: canvas.toDataURL(),
									});
								}
							)
						);
					}
				}
			});
		});
		Promise.allSettled(imagePromises)
			.then(() => {
				pptx.writeFile({
					fileName: `${pageModel.name.replaceAll(' ', '-')}.pptx`,
				})
					.then(() => {
						setIsExporting(false);
						closeWidgetsLibrarySidebar && closeWidgetsLibrarySidebar();
					})
					.catch(() => {
						console.log('export to pptx failed');
					});
			})
			.catch(() => {
				console.log('export to pptx failed');
			});
	}, 1500);
};

const calculateInches = (cssPixels: number) => {
	const devicePixelRatio = window.devicePixelRatio;
	const dotsPerInch = 96;
	const physicalPixels = cssPixels * devicePixelRatio;
	return physicalPixels / dotsPerInch;
};

const htmlToText = (htmlString: string): any[] => {
	const results: any[] = [];
	if (htmlString) {
		const nodes = new DOMParser().parseFromString(htmlString, 'text/html').body.childNodes;

		nodes.forEach((node) => {
			results.push([...getText(node)]);
		});
	}

	return results.flat();
};

const FORMAT_NODES = ['strong', 'em', 'ins'];
const getText = (
	node: ChildNode,
	parents: string[] = [],
	res: TextType[] = [],
	fontSize?: string
): TextType[] => {
	if (node.nodeName === '#text') {
		const text = node.textContent;
		if (text) {
			const format = parents.filter((p) => FORMAT_NODES.includes(p));
			res.push({
				text,
				format: format.length ? format : null,
				fontSize: fontSize ? parseInt(fontSize) - 5 : 16 - 5,
			});
		}
	} else {
		node.childNodes.forEach((node: ChildNode) => {
			const newFontSize = (node as HTMLElement).style?.fontSize;
			if (newFontSize) {
				fontSize = newFontSize.replace('px', '');
			}
			getText(node, parents.concat(node.nodeName.toLowerCase()), res, fontSize);
		});
	}
	return res;
};
